import type { LibraryItem, LibrarySuggestion, NodeJson, NodeType } from "../types";

type ItemNodes = Pick<LibraryItem, "nodesJson">;

type SymbolNodeJson = Extract<NodeJson, { type: "symbol" }>;

type TargetNode = NonNullable<LibrarySuggestion["targetNode"]>;

/**
 * What an item inserts: one node of its root's type, or an expression of several nodes.
 */
export type LibraryItemType = NodeType | "expression";

export function libraryItemType(item: ItemNodes): LibraryItemType {
  return itemRootNode(item)?.type ?? "expression";
}

/**
 * The one node an item inserts, when it inserts exactly one.
 */
export function itemRootNode(item: ItemNodes): NodeJson | undefined {
  return item.nodesJson.length === 1 ? item.nodesJson[0] : undefined;
}

/**
 * The root's symbol child, which carries the glyph and the symbol key, for an item that inserts
 * one node.
 */
export function itemSymbolNode(item: ItemNodes): SymbolNodeJson | undefined {
  return itemRootNode(item)?.children.find(
    (child): child is SymbolNodeJson => child.type === "symbol",
  );
}

/**
 * The nodes with the first root's preset labels removed, as typing `^` or `_` after a function
 * asks for; the same array when it has none.
 */
export function withoutLabels(nodesJson: NodeJson[]): NodeJson[] {
  const [root, ...rest] = nodesJson;
  const symbolIndex = root?.children.findIndex((child) => child.type === "symbol") ?? -1;
  const symbol = root?.children[symbolIndex];
  if (!root || !symbol?.children.some((child) => child.type === "label")) return nodesJson;

  const children = [...root.children];
  children[symbolIndex] = {
    ...symbol,
    children: symbol.children.filter((child) => child.type !== "label"),
  };

  return [{ ...root, children }, ...rest];
}

function segment(node: NodeJson, index: number): string {
  return `${node.type}:${index}`;
}

/**
 * Where the cursor goes after inserting a single-node item, as the editor places it for a node
 * built from props: a function into its first empty label, else its first parameter; a variable
 * into its first parameter. Nothing for a node without a slot, so the cursor stays after it.
 */
export function cursorTarget(nodes: NodeJson[]): TargetNode | undefined {
  const root = itemRootNode({ nodesJson: nodes });
  if (!root) return undefined;
  const rootPath = segment(root, 0);

  if (root.type === "function") {
    const symbolIndex = root.children.findIndex((child) => child.type === "symbol");
    const symbol = root.children[symbolIndex];
    const labelIndex = symbol?.children.findIndex(
      (child) => child.type === "label" && child.children[0]?.children.length === 0,
    );
    if (symbol && labelIndex !== undefined && labelIndex !== -1) {
      const path = [
        rootPath,
        segment(symbol, symbolIndex),
        segment(symbol.children[labelIndex], labelIndex),
        "inline:0",
      ];
      return { path: path.join("::"), index: 0 };
    }
  }

  if (root.type === "function" || root.type === "variable") {
    const parameterIndex = root.children.findIndex((child) => child.type === "inline");
    if (parameterIndex !== -1) {
      return {
        path: `${rootPath}::${segment(root.children[parameterIndex], parameterIndex)}`,
        index: 0,
      };
    }
  }

  return undefined;
}
